import React, { useEffect, useRef, useState } from "react";
import { Search, ChevronDown, User } from "lucide-react";
import { BsCalendarDate } from "react-icons/bs";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "../../components/calendartheme.css";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../context/AuthContext";
import { affiliateAPI } from "../../services/AffiliatePartnerPortal/affiliateAPI";

export default function CustomerList({profile}) {
  const { user } = useAuth();
  const datePickerRef = useRef(null);
  console.log(profile.contact,"++++++++++++++++++")

  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [filters, setFilters] = useState({
    status: "all",
    customerType: "all",
    dateRange: "all",
    city: "all",
  });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [loading, setLoading] = useState(false);

  const [startDate, endDate] = dateRange;

  // ✅ Fetch customers
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        console.log(user)
        if (!user?.id) return;
        const response = await affiliateAPI.getByUser(profile?.contact);
        const fetched = Array.isArray(response?.data) ? response.data : [];
        setCustomers(fetched);
        setFilteredCustomers(fetched);
      } catch (err) {
        console.error("Error fetching customers:", err);
        toast.error("Failed to fetch customers.");
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, [user?.id]);

  // ✅ Filter customers by search + date
  useEffect(() => {
    const filtered = (customers || []).filter((c) => {
      if (!c) return false;

      const matchesSearch =
        c?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c?.phone?.toLowerCase().includes(searchQuery.toLowerCase());

      const createdAt = c?.dateAdded ? new Date(c.dateAdded) : null;
      const matchesDate =
        (!startDate || (createdAt && createdAt >= startDate)) &&
        (!endDate || (createdAt && createdAt <= endDate));

      return matchesSearch && matchesDate;
    });

    setFilteredCustomers(filtered);
  }, [customers, searchQuery, startDate, endDate]);

  // ✅ Handle Select All
  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    if (newSelectAll) {
      setSelectedCustomers(filteredCustomers.map((c) => c._id));
    } else {
      setSelectedCustomers([]);
    }
  };

  // ✅ Handle Select Single
  const handleSelectCustomer = (id) => {
    setSelectedCustomers((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    );
  };

  // ✅ Export selected or all customers to Excel inside ZIP
  const exportToExcelAndZip = async () => {
    const customersToExport =
      selectedCustomers.length > 0
        ? filteredCustomers.filter((c) => selectedCustomers.includes(c._id))
        : filteredCustomers;

    if (!customersToExport || customersToExport.length === 0) {
      toast.warn("No customers to export.");
      return;
    }

    const wb = XLSX.utils.book_new();
    const dataForExcel = customersToExport.map((c) => ({
      "Full Name": c?.fullName || c?.name || "N/A",
      "Phone Number": c?.phoneNumber || "N/A",
      "WhatsApp Number": c?.whatsappNumber || "N/A",
      Relation: c?.relation || "N/A",
      "Date Added": c?.createdAt
        ? new Date(c.createdAt).toLocaleDateString()
        : "N/A",
      City: c?.permanentAddress?.city || "N/A",
    }));

    const ws = XLSX.utils.json_to_sheet(dataForExcel);
    XLSX.utils.book_append_sheet(wb, ws, "Customers");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const zip = new JSZip();
    zip.file("customers.xlsx", excelBuffer);

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "customers.zip";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ✅ Close calendar on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setIsDatePickerOpen(false);
      }
    };
    if (isDatePickerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDatePickerOpen]);

  return (
    <div>
      {/* <h3 className="text-xl font-bold mb-4">Customer List</h3> */}

      <div className="flex flex-wrap lg:flex-nowrap items-center gap-4 mb-4">
        {/* Search */}
        <div className="relative w-full sm:w-auto mb-3 sm:mb-0">
          <input
            type="text"
            placeholder="Search Customers"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-black border border-[#333333] rounded-full py-2 pl-10 pr-4 w-full"
          />
          <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
        </div>

        {/* Calendar Date Picker */}
        <div
          className="relative border border-[#333333] rounded-md"
          ref={datePickerRef}
        >
          <button
            className="flex items-center bg-transparent rounded-md px-2 sm:px-3 py-2 focus:outline-none text-sm sm:text-base"
            onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
          >
            <BsCalendarDate className="mr-1 sm:mr-2 text-[#FFCA20]" />
            <span>
              {startDate && endDate
                ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                : "Choose Dates"}
            </span>
          </button>

          {isDatePickerOpen && (
            <div className="absolute z-10 mt-2 left-0 bg-[#1e1e1e] rounded-lg p-2 border border-[#333333]">
              <Calendar
                selectRange={true}
                onChange={(update) => {
                  if (update[0] && update[1]) {
                    setDateRange(update);
                    setIsDatePickerOpen(false);
                  }
                }}
                value={[startDate, endDate]}
                calendarType="gregory"
                prev2Label={null}
                next2Label={null}
              />
            </div>
          )}
        </div>

        {/* Download ZIP */}
        <button
          onClick={exportToExcelAndZip}
          className="px-4 py-2 rounded-md bg-yellow-400 text-black font-bold"
        >
          Download as ZIP
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
        </div>
      ) : (
        <div className="overflow-x-auto scrollbar-hide">
          <table className="bg-[#131313] w-full text-center border border-[#333333]">
            <thead className="border-x border-[#333333]">
              <tr className="border-b border-[#333333]">
                <th className="pb-2 pr-4 border-x border-[#333333]">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="rounded border-[#333333]"
                  />
                </th>
                <th className="pb-2 pr-4 border-x border-[#333333]">NAME</th>
                <th className="pb-2 pr-4 border-x border-[#333333]">PHONE</th>
                <th className="pb-2 pr-4 border-x border-[#333333]">WHATSAPP</th>
                <th className="pb-2 pr-4 border-x border-[#333333]">RELATION</th>
                <th className="pb-2 pr-4 border-x border-[#333333]">DATE ADDED</th>
                <th className="pb-2 pr-4 border-x border-[#333333]">CITY</th>
              </tr>
            </thead>

             <tbody className="bg-[#1e1e1e] border border-[#333333]">
              {filteredCustomers.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-4 py-4 text-center text-[#F3F3F3]"
                  >
                    No customers found
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer) => (
                  <tr key={customer?._id}>
                    {/* Checkbox */}
                    <td className="border border-[#333333] px-4 py-3">
                      <input
                        type="checkbox"
                        className="h-5 w-5 cursor-pointer"
                        checked={selectedCustomers.includes(customer?._id)}
                        onChange={() => handleSelectCustomer(customer?._id)}
                      />
                    </td>

                    {/* Full Name */}
                    <td className="border border-[#333333] px-4 py-3 text-sm text-white">
                      {customer?.fullName || customer?.name || "N/A"}
                    </td>

                    {/* Phone Number */}
                    <td className="border border-[#333333] px-4 py-3 text-sm text-[#F3F3F3]">
                      {customer?.phoneNumber || "N/A"}
                    </td>

                    {/* WhatsApp Number */}
                    <td className="border border-[#333333] px-4 py-3 text-sm text-[#F3F3F3]">
                      {customer?.whatsappNumber || "N/A"}
                    </td>

                    {/* Relation */}
                    <td className="border border-[#333333] px-4 py-3 text-sm text-[#F3F3F3]">
                      {customer?.relation || "N/A"}
                    </td>

                    {/* Created Date */}
                    <td className="border border-[#333333] px-4 py-3 text-sm text-gray-300">
                      {customer?.createdAt
                        ? new Date(customer.createdAt).toLocaleDateString()
                        : "N/A"}
                    </td>

                    {/* City */}
                    <td className="border border-[#333333] px-4 py-3 text-sm text-[#F3F3F3]">
                      {customer?.permanentAddress?.city || "N/A"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
